/*
 * Shared dialog → pane send helper (Office.context.ui.messageParent), used by
 * every dialog-side Asset Library hook. Factored out of
 * useDialogSlideTemplates.ts so a second hook (useDialogBrandTemplates.ts)
 * doesn't duplicate the availability check + telemetry.
 */

import { logEvent, logError, scrubMessage } from "../../../../../telemetry";
import { serialiseMessage, type DialogToPaneMessage } from "./assetLibraryDialogChannel";

const DECKORATE_CONNECTOR = "deckorate";

/** Whether this window can talk back to the pane at all. */
export function canMessageParent(): boolean {
  return typeof Office !== "undefined" && typeof Office.context?.ui?.messageParent === "function";
}

/** Whether the host can push messages INTO this window (DialogApi 1.2+). */
export function canReceiveFromParent(): boolean {
  return typeof Office !== "undefined" && typeof Office.context?.ui?.addHandlerAsync === "function";
}

/** Sends `message` to the pane. Returns false (with a telemetry trace) if
 *  there's no channel to send it on — the caller decides how to degrade. */
export function postToPane(message: DialogToPaneMessage, reason: string): boolean {
  if (!canMessageParent()) {
    logEvent("connector.action.invoke", "warn", {
      connectorSystemName: DECKORATE_CONNECTOR,
      reason: "asset-library-message-parent-unavailable",
    });
    return false;
  }
  logEvent("connector.action.invoke", "info", { connectorSystemName: DECKORATE_CONNECTOR, reason });
  try {
    Office.context.ui.messageParent(serialiseMessage(message));
    return true;
  } catch (error) {
    // The dialog window (or its parent) can be mid-teardown when this fires;
    // messageParent can throw rather than silently no-op in that case.
    logError(
      "error.connector.failure",
      { type: error instanceof Error ? error.name : "MessageParentError", scrubbedMessage: scrubMessage(error instanceof Error ? error.message : String(error)) },
      { connectorSystemName: DECKORATE_CONNECTOR, reason: "asset-library-message-parent-threw" },
    );
    return false;
  }
}
